import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({ gfm: true, breaks: true });

// LLM outputs sometimes contain literal Python-repr-style unicode
// escapes — e.g. "\U672C\U5468..." for "本周...". This happens when
// the model picks an escape token instead of the character token
// mid-generation; users see the raw "\U672C" string in the rendered
// message. We decode both forms (uppercase \UXXXX from Python repr,
// lowercase \uXXXX from JSON) before markdown parsing so the
// characters render correctly. The lookbehind ensures real markdown
// escapes like `\\u` (showing the literal "\u") still pass through.
const UNICODE_ESCAPE = /(?<!\\)\\[uU]([0-9A-Fa-f]{4})/g;

export function decodeUnicodeEscapes(s: string): string {
  if (!s) return s;
  return s.replace(UNICODE_ESCAPE, (_, hex) =>
    String.fromCodePoint(parseInt(hex as string, 16))
  );
}

// Open all rendered <a> in a new tab + harden rel. Without this, the
// user clicking a citation/source link inside an AI bubble navigates the
// whole SPA away — losing their chat thread mid-research. The hook runs
// after sanitize, so the rel values we add can't be sanitized away.
//
// removeAllHooks first because vite HMR re-executes module-scope code
// on every save — without the removal, addHook stacks N copies that
// each set the same attributes, doing N× the work per anchor render.
DOMPurify.removeAllHooks();
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    (node as HTMLAnchorElement).setAttribute("target", "_blank");
    (node as HTMLAnchorElement).setAttribute("rel", "noopener noreferrer");
  }
});

export function renderMarkdown(src: string): string {
  if (!src) return "";
  const html = marked.parse(decodeUnicodeEscapes(src), { async: false }) as string;
  return DOMPurify.sanitize(html);
}

export function escapeHTML(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string
  ));
}

export function truncate(s: string, n: number): string {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

// stripMarkdown returns plain text from a small slice of markdown — the
// kind of slice the workspace AI-summary prompts emit (**bold**, line
// breaks, optional "- " bullet prefixes). For compact previews where
// line-clamp / single-line truncation matters and we can't afford the
// <p>/<br>/<ul> wrappers marked emits.
//
// Not a general markdown stripper: no link/image/code handling. If the
// scope grows beyond AI summaries, swap in marked with a plaintext
// renderer override instead of extending this.
export function stripMarkdown(s: string): string {
  if (!s) return "";
  return s
    .replace(/\*\*(.+?)\*\*/g, "$1")  // **bold** → bold
    .replace(/^\s*[-*]\s+/gm, "")     // leading "- " / "* " bullets → drop
    .replace(/\s*\n+\s*/g, " · ")    // newlines → middle-dot separators
    .trim();
}
